/**
 * EN / ES translations for the Lead Recovery service pages:
 * /lead-recovery (service overview) and /lead-recovery/hvac (industry page).
 * Imported and merged into the main translations object.
 */
export const leadRecoveryTranslations = {
  en: {
    /* ── /lead-recovery — service overview ─────────────────────── */
    leadRecoveryHub: {
      eyebrow: "Kranbos Lead Recovery",
      title: "Recover the Leads Your Business Already Generates",
      subtitle:
        "Kranbos builds lead recovery systems that help businesses recover missed calls, follow up on stalled estimates, re-engage unresponsive inquiries, and bring older leads back into the pipeline.",
      secondaryCta: "Contact Us",
      pillarsTitle: "Where Recovery Systems Create Value",
      pillar1Title: "Missed calls",
      pillar1Text:
        "Callers who reach voicemail get a fast, branded text response instead of silently moving on to a competitor.",
      pillar2Title: "Stalled estimates",
      pillar2Text:
        "Open estimates receive consistent, organized follow-up instead of depending on memory.",
      pillar3Title: "Unresponsive inquiries",
      pillar3Text:
        "Web leads and messages that went quiet are re-engaged through structured outreach.",
      pillar4Title: "Older leads",
      pillar4Text:
        "Past inquiries and customers are organized into relevant reactivation campaigns.",
      industriesTitle: "Industry-Specific Lead Recovery",
      industriesSubtitle:
        "Every industry loses leads in different places. We build recovery systems around the way each business actually receives and handles opportunities.",
      hvacCardTitle: "Lead Recovery for HVAC Companies",
      hvacCardText:
        "Recover missed calls, follow up on open estimates, and bring forgotten HVAC leads back into your pipeline.",
      hvacCardCta: "Explore HVAC Lead Recovery",
      moreIndustriesTitle: "More industries",
      moreIndustriesText:
        "Dedicated pages for plumbing, roofing, electrical, legal, dental, and other service businesses are on the way. The same recovery approach applies today — reach out and we'll tailor it to your industry.",
      moreIndustriesCta: "Contact Us About Your Industry",
      finalCtaTitle: "Not Sure Where Your Leads Are Leaking?",
      finalCtaText:
        "Start with a free 15-minute call. We'll review where opportunities may be slipping through and give you clear next steps.",
      homeLink: "Learn how our Lead Recovery service works",
    },

    /* ── /lead-recovery/hvac ───────────────────────────────────── */
    hvac: {
      breadcrumbHome: "Home",
      breadcrumbLeadRecovery: "Lead Recovery",
      breadcrumbHvac: "HVAC Lead Recovery",

      /* Section 1 — Hero */
      heroEyebrow: "Lead Recovery for HVAC Companies",
      heroTitle: "Stop Losing Good Leads After the Phone Rings",
      heroCopy1:
        "Your company may already be generating enough opportunities to grow. The problem is that some of those opportunities disappear between the first call and the booked job.",
      heroCopy2:
        "Kranbos builds customized lead recovery systems for HVAC companies. We help you respond to missed calls, follow up on open estimates, re-engage older leads, and understand what happens to every opportunity entering your business.",
      heroSecondaryCta: "See How It Works",
      flowStep1: "Homeowner calls",
      flowStep2: "Call is missed",
      flowStep3: "Immediate text response",
      flowStep4: "Team receives lead",
      flowStep5: "Follow-up continues",
      flowStep6: "Job booked",

      /* Section 2 — Problem recognition */
      problemTitle:
        "Your Marketing May Be Working. Your Follow-Up May Be Where the Revenue Disappears.",
      problemCopy:
        "Homeowners rarely wait patiently when their air conditioner stops working. When a call goes unanswered or an estimate receives no follow-up, the customer can move on to another contractor before your team has another opportunity to respond.",
      problemListTitle: "Lead leakage commonly happens when:",
      problemItems: [
        "Your office is handling multiple calls at once",
        "A customer calls after business hours",
        "Technicians receive inquiries while they are in the field",
        "Web leads arrive in an inbox that is not checked immediately",
        "Estimates remain open without a consistent follow-up process",
        "Older leads and past customers are never contacted again",
        "No one can clearly see which opportunities were contacted, booked, or lost",
      ],
      problemClosing:
        "Kranbos helps you create a reliable recovery process around those moments.",

      /* Section 3 — Opportunity calculator */
      calcTitle: "How Much Opportunity Is Slipping Through?",
      calcCopy1: "A missed opportunity does not always look like a lost lead.",
      calcCopy2:
        "It may be an unanswered phone call, an estimate that went quiet, a customer who asked to be called later, or a web inquiry that never reached the right person.",
      calcMissedLabel: "Estimated missed opportunities per month",
      calcBookingLabel: "Estimated booking rate (%)",
      calcJobValueLabel: "Average job value ($)",
      calcMonthlyResult: "Estimated monthly revenue at risk",
      calcAnnualResult: "Estimated annual revenue at risk",
      calcDisclaimer:
        "These figures are estimates for planning purposes only — not a guarantee of recovered revenue.",
      calcCta: "Book a free 15-min call",

      /* Section 4 — Solution overview */
      solutionTitle:
        "A Recovery System Built Around the Way Your Company Already Works",
      solution1Title: "Missed-Call Recovery",
      solution1Text:
        "When your company cannot answer a call, the caller can receive a fast, branded text acknowledging the call and offering a clear next step.",
      solution2Title: "Estimate Follow-Up",
      solution2Text:
        "Open estimates can receive timely, consistent follow-up without relying entirely on someone remembering to send another message.",
      solution3Title: "Lead Reactivation",
      solution3Text:
        "Older inquiries and past customers can be organized into relevant reactivation campaigns based on their history and service needs.",
      solution4Title: "Call and Source Tracking",
      solution4Text:
        "Understand where calls originate, when they go unanswered, and what happens after the initial inquiry.",
      solution5Title: "Pipeline Visibility",
      solution5Text:
        "Give your team a clearer view of new, contacted, quoted, recovered, booked, and lost opportunities.",

      /* Section 5 — How it works */
      howTitle: "How Kranbos Builds Your Lead Recovery System",
      how1Title: "Find the leaks",
      how1Text:
        "We review how your company currently handles inbound calls, web inquiries, estimates, follow-up, and lead handoffs. We identify where opportunities are slowing down or disappearing.",
      how2Title: "Build the recovery process",
      how2Text:
        "We configure the workflows, tracking, messaging, routing, notifications, and integrations needed to support your team.",
      how3Title: "Measure and improve",
      how3Text:
        "We monitor how the system performs and identify opportunities to improve response time, customer engagement, staff handoffs, and reporting.",
      howNote:
        "Where practical, Kranbos works with your existing systems rather than automatically forcing a full software replacement.",

      /* Section 6 — Deliverables and pricing */
      deliverablesTitle: "What Your Lead Recovery Setup Can Include",
      deliverablesIntro:
        "Depending on the client's existing systems and needs, the setup may include:",
      deliverablesItems: [
        "Lead-flow and missed-opportunity audit",
        "Call tracking and routing configuration",
        "Missed-call text-back",
        "Two-way customer texting",
        "After-hours response workflows",
        "Estimate follow-up sequences",
        "Lead-status and pipeline setup",
        "Staff alerts and assignment rules",
        "Message and response templates",
        "Reporting and attribution dashboard",
        "Team training and documentation",
        "Initial testing and optimization",
      ],
      pricingHeadline: "Every setup is scoped to your business",
      pricingDescription1:
        "We scope it with you on a free 15-minute call, based on call volume, number of locations, existing systems, integrations, and the recovery workflows your company requires.",
      pricingDescription2:
        "Third-party software, phone service, messaging, CRM, and usage-based subscriptions are billed separately. All expected costs are reviewed with you before implementation.",

      /* Section 7 — Ideal client */
      idealTitle: "Designed for HVAC Companies With Leads Already Coming In",
      idealItems: [
        "You have a consistent flow of inbound calls or estimate requests",
        "Your team cannot answer every call immediately",
        "You invest in Google Ads, Local Services Ads, SEO, referrals, or direct mail",
        "Some estimates go quiet after the initial appointment",
        "Lead information is spread across different tools",
        "You cannot easily see which leads received proper follow-up",
        "You want more value from your current demand before increasing advertising spend",
      ],
      idealHighlight:
        "This is not primarily a lead-generation service. It is a system for helping your company convert more of the opportunities it already receives.",

      /* Section 8 — Measurement and reporting */
      measureTitle: "What We Measure",
      measureIntro:
        "A recovery system should create visibility, not just send automated messages.",
      measureSubtitle:
        "Depending on the systems available, reporting may include:",
      measureItems: [
        "Inbound and missed calls",
        "Response time",
        "Leads contacted",
        "Customer replies",
        "Conversations restarted",
        "Estimates re-engaged",
        "Appointments requested",
        "Jobs connected to recovered opportunities",
        "Reasons opportunities were lost",
      ],
      measureClosing:
        "The goal is to give you a clearer picture of how leads are handled and where the biggest opportunities for improvement remain.",

      /* Section 9 — Before and after */
      compareTitle: "Before and After Lead Recovery",
      compareWithoutTitle: "Without a Recovery System",
      compareWithoutItems: [
        "Missed calls reach voicemail",
        "Estimates depend on someone remembering to follow up",
        "Lead information is scattered across phones, inboxes, and spreadsheets",
        "Marketing creates inquiries, but the final outcome is unclear",
        "Owners and managers have to assume follow-up happened",
      ],
      compareWithTitle: "With a Recovery System",
      compareWithItems: [
        "Missed callers receive a clear and timely response",
        "Follow-up occurs through an organized process",
        "Opportunities are assigned and visible",
        "Calls and outcomes can be connected more clearly",
        "Owners and managers can see where leads stand",
      ],

      /* Section 10 — FAQ */
      faqTitle: "Frequently Asked Questions",
      faqItems: [
        {
          q: "What is HVAC lead recovery?",
          a: "HVAC lead recovery is the process of identifying and re-engaging potential customers who contacted your business but did not schedule or complete a service. This can include missed callers, unresponsive web leads, open estimates, and older inquiries.",
        },
        {
          q: "How does missed-call text-back work?",
          a: "When an eligible inbound call goes unanswered, the system sends a branded text message acknowledging the call and giving the customer a way to continue the conversation. The lead can then be routed to the appropriate team member for follow-up.",
        },
        {
          q: "Will I need to change my phone number?",
          a: "Not necessarily. The available options depend on your existing phone provider, call flow, tracking requirements, and integrations. We evaluate this during the initial assessment.",
        },
        {
          q: "Can Kranbos work with my existing CRM?",
          a: "In many cases, yes. We first review your current CRM, phone system, scheduling platform, and workflow. When an appropriate integration is available, we can design the recovery process around your existing technology.",
        },
        {
          q: "Can the system follow up on open estimates?",
          a: "Yes. Estimate follow-up can be included when the necessary estimate and customer information is available from your existing tools.",
        },
        {
          q: "Does it work after hours?",
          a: "After-hours recovery is one of the situations the system can address. The exact response and handoff process is customized around your hours, emergency policies, and team availability.",
        },
        {
          q: "Is this an AI answering service?",
          a: "Not necessarily. AI voice handling may be considered when it supports your goals, but the lead recovery system can also use call tracking, text messaging, CRM workflows, notifications, and human follow-up. We recommend tools based on the business problem rather than adding AI unnecessarily.",
        },
        {
          q: "What subscriptions will I need?",
          a: "The setup may require third-party services for call tracking, phone numbers, messaging, workflow automation, CRM functionality, or reporting. These subscriptions are separate from the Kranbos implementation fee. Clients receive a clear overview of anticipated software and usage costs before approving the system.",
        },
        {
          q: "How much does HVAC lead recovery cost?",
          a: "It depends on the complexity of the call flow, number of users and locations, integrations, and follow-up requirements. We scope it with you on a free 15-minute call.",
        },
        {
          q: "Does Kranbos generate new leads?",
          a: "Lead recovery is focused primarily on converting more of the demand a business already generates. Additional marketing or lead-generation services can be evaluated separately.",
        },
        {
          q: "How do you know whether the system is working?",
          a: "We establish trackable stages and outcomes based on the information available from the phone, messaging, CRM, and scheduling systems. This helps show which opportunities were contacted, re-engaged, booked, or lost.",
        },
      ],

      /* Section 11 — Final CTA */
      finalCtaTitle:
        "Before You Spend More to Generate Leads, Find Out What Is Happening to the Ones You Already Have.",
      finalCtaCopy:
        "We'll review how calls and estimates move through your business, identify likely recovery opportunities, and show you what a practical system could look like.",

      /* Back-link */
      backToLeadRecovery: "Learn about Kranbos Lead Recovery for all industries",

      /* Audit form */
      form: {
        title: "Prefer we reach out?",
        subtitle:
          "Tell us a bit about your HVAC company and how leads reach you today. We'll get back to you to schedule a free 15-minute call.",
        submitLabel: "Request a call",
        successTitle: "Request received!",
        successMessage:
          "Thanks — we'll get back to you within one business day. You can also book your free 15-minute call now.",
        bookCall: "Book a free 15-min call",
        fields: {
          fullName: "Name",
          companyName: "Company name",
          email: "Email",
          phone: "Phone number",
          website: "Company website",
          serviceArea: "Service area",
          serviceAreaPlaceholder: "e.g. Miami, FL metro area",
          monthlyVolume: "Approximate inbound calls per month",
          monthlyVolumePlaceholder: "e.g. 150",
          teamSize: "Approximate number of technicians",
          teamSizePlaceholder: "e.g. 6",
          phoneSystem: "Current phone system or call-tracking platform",
          phoneSystemPlaceholder: "e.g. RingCentral, CallRail, landline",
          currentTools: "Current CRM or field-service platform",
          currentToolsPlaceholder: "e.g. ServiceTitan, Housecall Pro, Jobber",
          primaryIssue: "Primary issue",
          primaryIssueOptions: {
            missed_calls: "Missed calls",
            slow_lead_response: "Slow lead response",
            unsold_estimates: "Unsold estimates",
            old_lead_reactivation: "Old lead reactivation",
            lack_of_reporting: "Lack of reporting",
            multiple_issues: "Multiple issues",
          },
          message: "Anything else we should know? (optional)",
          messagePlaceholder: "Tell us more about your situation...",
        },
      },
    },
  },

  es: {
    /* ── /lead-recovery — resumen del servicio ─────────────────── */
    leadRecoveryHub: {
      eyebrow: "Recuperación de Leads Kranbos",
      title: "Recupera los Leads que tu Negocio ya Genera",
      subtitle:
        "Kranbos construye sistemas de recuperación de leads que ayudan a los negocios a recuperar llamadas perdidas, dar seguimiento a presupuestos estancados, reactivar consultas sin respuesta y traer leads antiguos de vuelta al pipeline.",
      secondaryCta: "Contáctanos",
      pillarsTitle: "Dónde Crean Valor los Sistemas de Recuperación",
      pillar1Title: "Llamadas perdidas",
      pillar1Text:
        "Quienes llegan al buzón de voz reciben una respuesta rápida por texto con tu marca, en lugar de irse en silencio con la competencia.",
      pillar2Title: "Presupuestos estancados",
      pillar2Text:
        "Los presupuestos abiertos reciben seguimiento constante y organizado, sin depender de la memoria.",
      pillar3Title: "Consultas sin respuesta",
      pillar3Text:
        "Los leads web y mensajes que quedaron en silencio se reactivan mediante contacto estructurado.",
      pillar4Title: "Leads antiguos",
      pillar4Text:
        "Las consultas pasadas y clientes anteriores se organizan en campañas de reactivación relevantes.",
      industriesTitle: "Recuperación de Leads por Industria",
      industriesSubtitle:
        "Cada industria pierde leads en lugares distintos. Construimos sistemas de recuperación en torno a la forma real en que cada negocio recibe y gestiona sus oportunidades.",
      hvacCardTitle: "Recuperación de Leads para Empresas de HVAC",
      hvacCardText:
        "Recupera llamadas perdidas, da seguimiento a presupuestos abiertos y trae de vuelta a tu pipeline los leads de HVAC olvidados.",
      hvacCardCta: "Explorar Recuperación de Leads HVAC",
      moreIndustriesTitle: "Más industrias",
      moreIndustriesText:
        "Próximamente habrá páginas dedicadas para plomería, techado, electricidad, legal, dental y otros negocios de servicios. El mismo enfoque de recuperación aplica hoy — contáctanos y lo adaptamos a tu industria.",
      moreIndustriesCta: "Consultar por tu Industria",
      finalCtaTitle: "¿No sabes por dónde se fugan tus leads?",
      finalCtaText:
        "Empieza con una llamada gratis de 15 minutos. Revisaremos dónde se pueden estar escapando oportunidades y te daremos próximos pasos claros.",
      homeLink: "Descubre cómo funciona nuestro servicio de Recuperación de Leads",
    },

    /* ── /lead-recovery/hvac ───────────────────────────────────── */
    hvac: {
      breadcrumbHome: "Inicio",
      breadcrumbLeadRecovery: "Recuperación de Leads",
      breadcrumbHvac: "Recuperación de Leads HVAC",

      heroEyebrow: "Recuperación de Leads para Empresas de HVAC",
      heroTitle: "Deja de Perder Buenos Leads Después de que Suena el Teléfono",
      heroCopy1:
        "Es posible que tu empresa ya genere suficientes oportunidades para crecer. El problema es que algunas de esas oportunidades desaparecen entre la primera llamada y el trabajo agendado.",
      heroCopy2:
        "Kranbos construye sistemas personalizados de recuperación de leads para empresas de HVAC. Te ayudamos a responder llamadas perdidas, dar seguimiento a presupuestos abiertos, reactivar leads antiguos y entender qué sucede con cada oportunidad que entra a tu negocio.",
      heroSecondaryCta: "Ver Cómo Funciona",
      flowStep1: "El cliente llama",
      flowStep2: "La llamada se pierde",
      flowStep3: "Respuesta inmediata por texto",
      flowStep4: "El equipo recibe el lead",
      flowStep5: "El seguimiento continúa",
      flowStep6: "Trabajo agendado",

      problemTitle:
        "Tu Marketing Puede Estar Funcionando. Tu Seguimiento Puede Ser Donde Desaparecen los Ingresos.",
      problemCopy:
        "Los propietarios rara vez esperan con paciencia cuando su aire acondicionado deja de funcionar. Cuando una llamada no se contesta o un presupuesto no recibe seguimiento, el cliente puede irse con otro contratista antes de que tu equipo tenga otra oportunidad de responder.",
      problemListTitle: "La fuga de leads suele ocurrir cuando:",
      problemItems: [
        "Tu oficina atiende varias llamadas a la vez",
        "Un cliente llama fuera del horario de atención",
        "Los técnicos reciben consultas mientras están en campo",
        "Los leads web llegan a una bandeja que no se revisa de inmediato",
        "Los presupuestos quedan abiertos sin un proceso de seguimiento constante",
        "Los leads antiguos y clientes anteriores nunca vuelven a ser contactados",
        "Nadie puede ver con claridad qué oportunidades fueron contactadas, agendadas o perdidas",
      ],
      problemClosing:
        "Kranbos te ayuda a crear un proceso de recuperación confiable alrededor de esos momentos.",

      calcTitle: "¿Cuánta Oportunidad se Está Escapando?",
      calcCopy1: "Una oportunidad perdida no siempre parece un lead perdido.",
      calcCopy2:
        "Puede ser una llamada sin contestar, un presupuesto que quedó en silencio, un cliente que pidió que lo llamaran después, o una consulta web que nunca llegó a la persona correcta.",
      calcMissedLabel: "Oportunidades perdidas estimadas por mes",
      calcBookingLabel: "Tasa de cierre estimada (%)",
      calcJobValueLabel: "Valor promedio del trabajo ($)",
      calcMonthlyResult: "Ingresos mensuales estimados en riesgo",
      calcAnnualResult: "Ingresos anuales estimados en riesgo",
      calcDisclaimer:
        "Estas cifras son estimaciones solo con fines de planificación — no una garantía de ingresos recuperados.",
      calcCta: "Agenda una llamada gratis de 15 min",

      solutionTitle:
        "Un Sistema de Recuperación Construido Alrededor de Cómo ya Trabaja tu Empresa",
      solution1Title: "Recuperación de Llamadas Perdidas",
      solution1Text:
        "Cuando tu empresa no puede contestar una llamada, quien llama puede recibir un texto rápido con tu marca reconociendo la llamada y ofreciendo un siguiente paso claro.",
      solution2Title: "Seguimiento de Presupuestos",
      solution2Text:
        "Los presupuestos abiertos pueden recibir seguimiento oportuno y constante sin depender completamente de que alguien recuerde enviar otro mensaje.",
      solution3Title: "Reactivación de Leads",
      solution3Text:
        "Las consultas antiguas y clientes anteriores pueden organizarse en campañas de reactivación relevantes según su historial y necesidades de servicio.",
      solution4Title: "Seguimiento de Llamadas y Fuentes",
      solution4Text:
        "Entiende de dónde vienen las llamadas, cuándo quedan sin contestar y qué sucede después de la consulta inicial.",
      solution5Title: "Visibilidad del Pipeline",
      solution5Text:
        "Dale a tu equipo una vista más clara de las oportunidades nuevas, contactadas, cotizadas, recuperadas, agendadas y perdidas.",

      howTitle: "Cómo Construye Kranbos tu Sistema de Recuperación de Leads",
      how1Title: "Encontrar las fugas",
      how1Text:
        "Revisamos cómo tu empresa maneja actualmente las llamadas entrantes, consultas web, presupuestos, seguimiento y traspasos de leads. Identificamos dónde las oportunidades se frenan o desaparecen.",
      how2Title: "Construir el proceso de recuperación",
      how2Text:
        "Configuramos los flujos de trabajo, el seguimiento, la mensajería, el enrutamiento, las notificaciones y las integraciones necesarias para apoyar a tu equipo.",
      how3Title: "Medir y mejorar",
      how3Text:
        "Monitoreamos el rendimiento del sistema e identificamos oportunidades para mejorar el tiempo de respuesta, la interacción con clientes, los traspasos entre el equipo y los reportes.",
      howNote:
        "Cuando es práctico, Kranbos trabaja con tus sistemas existentes en lugar de forzar automáticamente un reemplazo total de software.",

      deliverablesTitle: "Qué Puede Incluir tu Instalación de Recuperación de Leads",
      deliverablesIntro:
        "Según los sistemas existentes y las necesidades del cliente, la instalación puede incluir:",
      deliverablesItems: [
        "Auditoría de flujo de leads y oportunidades perdidas",
        "Configuración de seguimiento y enrutamiento de llamadas",
        "Respuesta por texto a llamadas perdidas",
        "Mensajes de texto bidireccionales con clientes",
        "Flujos de respuesta fuera de horario",
        "Secuencias de seguimiento de presupuestos",
        "Configuración de estados de leads y pipeline",
        "Alertas y reglas de asignación para el equipo",
        "Plantillas de mensajes y respuestas",
        "Panel de reportes y atribución",
        "Capacitación del equipo y documentación",
        "Pruebas iniciales y optimización",
      ],
      pricingHeadline: "Cada instalación se adapta a tu negocio",
      pricingDescription1:
        "La definimos contigo en una llamada gratis de 15 minutos, según el volumen de llamadas, número de ubicaciones, sistemas existentes, integraciones y los flujos de recuperación que tu empresa requiera.",
      pricingDescription2:
        "El software de terceros, servicio telefónico, mensajería, CRM y suscripciones por uso se facturan por separado. Todos los costos esperados se revisarán antes de la implementación.",

      idealTitle: "Diseñado para Empresas de HVAC que ya Reciben Leads",
      idealItems: [
        "Tienes un flujo constante de llamadas entrantes o solicitudes de presupuesto",
        "Tu equipo no puede contestar cada llamada de inmediato",
        "Inviertes en Google Ads, Local Services Ads, SEO, referidos o correo directo",
        "Algunos presupuestos quedan en silencio después de la cita inicial",
        "La información de los leads está repartida entre distintas herramientas",
        "No puedes ver fácilmente qué leads recibieron un seguimiento adecuado",
        "Quieres más valor de tu demanda actual antes de aumentar el gasto en publicidad",
      ],
      idealHighlight:
        "Este no es principalmente un servicio de generación de leads. Es un sistema para ayudar a tu empresa a convertir más de las oportunidades que ya recibe.",

      measureTitle: "Qué Medimos",
      measureIntro:
        "Un sistema de recuperación debe crear visibilidad, no solo enviar mensajes automáticos.",
      measureSubtitle:
        "Según los sistemas disponibles, los reportes pueden incluir:",
      measureItems: [
        "Llamadas entrantes y perdidas",
        "Tiempo de respuesta",
        "Leads contactados",
        "Respuestas de clientes",
        "Conversaciones reiniciadas",
        "Presupuestos reactivados",
        "Citas solicitadas",
        "Trabajos conectados a oportunidades recuperadas",
        "Razones por las que se perdieron oportunidades",
      ],
      measureClosing:
        "El objetivo es darte una imagen más clara de cómo se gestionan los leads y dónde quedan las mayores oportunidades de mejora.",

      compareTitle: "Antes y Después de la Recuperación de Leads",
      compareWithoutTitle: "Sin un Sistema de Recuperación",
      compareWithoutItems: [
        "Las llamadas perdidas llegan al buzón de voz",
        "Los presupuestos dependen de que alguien recuerde dar seguimiento",
        "La información de los leads está dispersa entre teléfonos, bandejas de entrada y hojas de cálculo",
        "El marketing genera consultas, pero el resultado final no está claro",
        "Los dueños y gerentes tienen que asumir que el seguimiento ocurrió",
      ],
      compareWithTitle: "Con un Sistema de Recuperación",
      compareWithItems: [
        "Quienes llaman y no son atendidos reciben una respuesta clara y oportuna",
        "El seguimiento ocurre mediante un proceso organizado",
        "Las oportunidades están asignadas y visibles",
        "Las llamadas y los resultados pueden conectarse con mayor claridad",
        "Los dueños y gerentes pueden ver en qué punto está cada lead",
      ],

      faqTitle: "Preguntas Frecuentes",
      faqItems: [
        {
          q: "¿Qué es la recuperación de leads de HVAC?",
          a: "La recuperación de leads de HVAC es el proceso de identificar y reactivar clientes potenciales que contactaron a tu negocio pero no agendaron ni completaron un servicio. Esto puede incluir llamadas perdidas, leads web sin respuesta, presupuestos abiertos y consultas antiguas.",
        },
        {
          q: "¿Cómo funciona la respuesta por texto a llamadas perdidas?",
          a: "Cuando una llamada entrante elegible no se contesta, el sistema envía un mensaje de texto con tu marca reconociendo la llamada y dando al cliente una forma de continuar la conversación. El lead puede luego dirigirse al miembro del equipo apropiado para el seguimiento.",
        },
        {
          q: "¿Tendré que cambiar mi número de teléfono?",
          a: "No necesariamente. Las opciones disponibles dependen de tu proveedor telefónico actual, el flujo de llamadas, los requisitos de seguimiento y las integraciones. Lo evaluamos durante la evaluación inicial.",
        },
        {
          q: "¿Puede Kranbos trabajar con mi CRM actual?",
          a: "En muchos casos, sí. Primero revisamos tu CRM, sistema telefónico, plataforma de agendamiento y flujo de trabajo actuales. Cuando hay una integración apropiada disponible, podemos diseñar el proceso de recuperación alrededor de tu tecnología existente.",
        },
        {
          q: "¿Puede el sistema dar seguimiento a presupuestos abiertos?",
          a: "Sí. El seguimiento de presupuestos puede incluirse cuando la información necesaria del presupuesto y del cliente está disponible desde tus herramientas actuales.",
        },
        {
          q: "¿Funciona fuera del horario de atención?",
          a: "La recuperación fuera de horario es una de las situaciones que el sistema puede abordar. El proceso exacto de respuesta y traspaso se personaliza según tus horarios, políticas de emergencia y disponibilidad del equipo.",
        },
        {
          q: "¿Es esto un servicio de contestador con IA?",
          a: "No necesariamente. El manejo de voz con IA puede considerarse cuando apoya tus objetivos, pero el sistema de recuperación de leads también puede usar seguimiento de llamadas, mensajes de texto, flujos de CRM, notificaciones y seguimiento humano. Recomendamos herramientas según el problema de negocio, en lugar de agregar IA innecesariamente.",
        },
        {
          q: "¿Qué suscripciones necesitaré?",
          a: "La instalación puede requerir servicios de terceros para seguimiento de llamadas, números telefónicos, mensajería, automatización de flujos, funcionalidad de CRM o reportes. Estas suscripciones son independientes de la tarifa de implementación de Kranbos. Los clientes reciben un resumen claro de los costos anticipados de software y uso antes de aprobar el sistema.",
        },
        {
          q: "¿Cuánto cuesta la recuperación de leads de HVAC?",
          a: "Depende de la complejidad del flujo de llamadas, el número de usuarios y ubicaciones, las integraciones y los requisitos de seguimiento. Lo definimos contigo en una llamada gratis de 15 minutos.",
        },
        {
          q: "¿Kranbos genera leads nuevos?",
          a: "La recuperación de leads se enfoca principalmente en convertir más de la demanda que un negocio ya genera. Los servicios adicionales de marketing o generación de leads pueden evaluarse por separado.",
        },
        {
          q: "¿Cómo saben si el sistema está funcionando?",
          a: "Establecemos etapas y resultados rastreables según la información disponible de los sistemas de teléfono, mensajería, CRM y agendamiento. Esto ayuda a mostrar qué oportunidades fueron contactadas, reactivadas, agendadas o perdidas.",
        },
      ],

      finalCtaTitle:
        "Antes de Gastar Más en Generar Leads, Descubre Qué Está Pasando con los que ya Tienes.",
      finalCtaCopy:
        "Revisaremos cómo se mueven las llamadas y presupuestos por tu negocio, identificaremos oportunidades probables de recuperación y te mostraremos cómo podría verse un sistema práctico.",

      backToLeadRecovery:
        "Conoce la Recuperación de Leads de Kranbos para todas las industrias",

      form: {
        title: "¿Prefieres que te contactemos?",
        subtitle:
          "Cuéntanos un poco sobre tu empresa de HVAC y cómo te llegan los leads hoy. Te contactaremos para agendar una llamada gratis de 15 minutos.",
        submitLabel: "Solicitar una llamada",
        successTitle: "¡Solicitud recibida!",
        successMessage:
          "Gracias — te responderemos dentro de un día hábil. También puedes agendar tu llamada gratis de 15 minutos ahora.",
        bookCall: "Agenda una llamada gratis de 15 min",
        fields: {
          fullName: "Nombre",
          companyName: "Nombre de la empresa",
          email: "Email",
          phone: "Número de teléfono",
          website: "Sitio web de la empresa",
          serviceArea: "Área de servicio",
          serviceAreaPlaceholder: "ej. Área metropolitana de Miami, FL",
          monthlyVolume: "Llamadas entrantes aproximadas por mes",
          monthlyVolumePlaceholder: "ej. 150",
          teamSize: "Número aproximado de técnicos",
          teamSizePlaceholder: "ej. 6",
          phoneSystem: "Sistema telefónico o plataforma de seguimiento de llamadas actual",
          phoneSystemPlaceholder: "ej. RingCentral, CallRail, línea fija",
          currentTools: "CRM o plataforma de servicio en campo actual",
          currentToolsPlaceholder: "ej. ServiceTitan, Housecall Pro, Jobber",
          primaryIssue: "Problema principal",
          primaryIssueOptions: {
            missed_calls: "Llamadas perdidas",
            slow_lead_response: "Respuesta lenta a leads",
            unsold_estimates: "Presupuestos sin cerrar",
            old_lead_reactivation: "Reactivación de leads antiguos",
            lack_of_reporting: "Falta de reportes",
            multiple_issues: "Varios problemas",
          },
          message: "¿Algo más que debamos saber? (opcional)",
          messagePlaceholder: "Cuéntanos más sobre tu situación...",
        },
      },
    },
  },
};
